import React, { useCallback, useEffect, useState } from 'react';
import { AppNav } from '../../components/layout/AppNav';
import { adminService, type AdminReseller, type FeedbackEntry } from '../../services/admin.service';
import { Star, Download, X, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { useToast } from '../../components/ui/Toast';

function StarRating({ value, size = 14 }: { value: number; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size} fill={i < value ? '#F59E0B' : 'none'} color={i < value ? '#F59E0B' : '#D1D5DB'} />
      ))}
    </span>
  );
}

function ScoreChip({ value }: { value: number }) {
  const color = value >= 4 ? '#16a34a' : value >= 3 ? '#d97706' : '#dc2626';
  const bg = value >= 4 ? '#d1fae5' : value >= 3 ? '#fef3c7' : '#fee2e2';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', background: bg, color, borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
      <Star size={10} fill={color} color={color} /> {value}
    </span>
  );
}

const PAGE_SIZE = 20;

export const AdminFeedbackPage: React.FC = () => {
  const { showToast } = useToast();

  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [resellers, setResellers] = useState<AdminReseller[]>([]);

  // Filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [resellerId, setResellerId] = useState('');
  const [rating, setRating] = useState('');

  const hasFilter = from || to || resellerId || rating;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await adminService.getFeedback({
        from: from || undefined,
        to: to || undefined,
        resellerId: resellerId || undefined,
        rating: rating ? Number(rating) : undefined,
        page: p,
        limit: PAGE_SIZE,
      });
      setEntries(res.data);
      setTotal(res.total);
      setPage(p);
    } catch {
      showToast('Failed to load feedback', 'error');
    } finally {
      setLoading(false);
    }
  }, [from, to, resellerId, rating, showToast]);

  useEffect(() => { load(1); }, [load]);

  useEffect(() => {
    adminService.getResellers().then(setResellers).catch(() => {});
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      adminService.exportFeedback({ from: from || undefined, to: to || undefined, resellerId: resellerId || undefined });
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => { setFrom(''); setTo(''); setResellerId(''); setRating(''); };

  // Summary stats from current result set
  const avgRating = entries.length > 0
    ? (entries.reduce((s, e) => s + e.rating, 0) / entries.length).toFixed(1)
    : null;
  const dist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  entries.forEach((e) => { if (e.rating >= 1 && e.rating <= 5) dist[e.rating]++; });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Feedback" subtitle="Agent & reseller satisfaction reviews" />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

        {/* ── Filter bar ────────────────────────────────────────────────── */}
        <div className="card" style={{ padding: '16px 20px', marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label className="lbl" style={{ marginBottom: 4, display: 'block' }}>From</label>
            <input type="date" className="inp" value={from} onChange={(e) => setFrom(e.target.value)}
              style={{ width: 150, fontSize: 13 }} />
          </div>
          <div>
            <label className="lbl" style={{ marginBottom: 4, display: 'block' }}>To</label>
            <input type="date" className="inp" value={to} onChange={(e) => setTo(e.target.value)}
              style={{ width: 150, fontSize: 13 }} />
          </div>
          <div>
            <label className="lbl" style={{ marginBottom: 4, display: 'block' }}>Reseller</label>
            <select className="inp" value={resellerId} onChange={(e) => setResellerId(e.target.value)} style={{ width: 180, fontSize: 13 }}>
              <option value="">All Resellers</option>
              {resellers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="lbl" style={{ marginBottom: 4, display: 'block' }}>Rating</label>
            <select className="inp" value={rating} onChange={(e) => setRating(e.target.value)} style={{ width: 130, fontSize: 13 }}>
              <option value="">All Ratings</option>
              {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} {r === 1 ? 'star' : 'stars'}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            {hasFilter && (
              <button className="btn-ghost btn-sm" onClick={clearFilters} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <X size={13} /> Clear
              </button>
            )}
            <button
              className="btn-primary"
              onClick={handleExport}
              disabled={exporting}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
            >
              <Download size={13} />
              {exporting ? 'Exporting…' : hasFilter ? 'Export Filtered' : 'Export All'}
            </button>
          </div>
        </div>

        {/* ── Summary strip ─────────────────────────────────────────────── */}
        {!loading && entries.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div className="metric-tile" style={{ borderTop: '3px solid #F59E0B' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#F59E0B' }}>{avgRating} <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)' }}>/ 5</span></div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>Avg. Rating {hasFilter ? '(filtered)' : ''}</div>
            </div>
            <div className="metric-tile" style={{ borderTop: '3px solid var(--p)' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--p)' }}>{total}</div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>Total Responses</div>
            </div>
            {[5, 4, 3].map((r) => (
              <div key={r} className="metric-tile" style={{ borderTop: `3px solid ${r >= 4 ? '#16a34a' : '#d97706'}` }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: r >= 4 ? '#16a34a' : '#d97706' }}>{dist[r]}</div>
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{r}-star reviews</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Table ─────────────────────────────────────────────────────── */}
        <div className="card" style={{ overflow: 'hidden', marginBottom: 16 }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>Loading…</div>
          ) : entries.length === 0 ? (
            <div className="empty-state" style={{ padding: '48px 24px' }}>
              <div className="empty-state-icon"><MessageSquare size={26} /></div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 }}>No feedback found</div>
              <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
                {hasFilter ? 'Try adjusting your filters.' : 'No feedback has been submitted yet.'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Offer #</th>
                    <th>Reseller</th>
                    <th>Agent</th>
                    <th>Rating</th>
                    <th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((fb) => (
                    <tr key={fb.id}>
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--text-2)', fontSize: 12.5 }}>
                        {new Date(fb.createdAt).toLocaleDateString('en-SA', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td style={{ fontWeight: 600, color: 'var(--p)', whiteSpace: 'nowrap' }}>{fb.offerNumber}</td>
                      <td style={{ fontSize: 13 }}>{fb.resellerName}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--p-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--p)', flexShrink: 0 }}>
                            {(fb.agentName || '?').charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontSize: 13 }}>{fb.agentName}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <ScoreChip value={fb.rating} />
                          <StarRating value={fb.rating} size={12} />
                        </div>
                      </td>
                      <td style={{ maxWidth: 320 }}>
                        {fb.comment ? (
                          <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontStyle: 'italic' }}>"{fb.comment}"</span>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Pagination ────────────────────────────────────────────────── */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: 'var(--text-3)' }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="btn-ghost btn-sm"
                onClick={() => load(page - 1)}
                disabled={page === 1}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span style={{ padding: '5px 12px', background: 'var(--p)', color: '#fff', borderRadius: 6, fontSize: 12.5, fontWeight: 600 }}>
                {page} / {totalPages}
              </span>
              <button
                className="btn-ghost btn-sm"
                onClick={() => load(page + 1)}
                disabled={page === totalPages}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
